import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { Incident } from "@/contracts";

import { acknowledgeIncidentMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { TextAreaField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { INCIDENT_TYPE_LABELS, formatIncidentValue } from "@/features/devices/device-labels";
import { useApiClient } from "@/lib/api-context";
import { formatDateTime } from "@/lib/format";

const acknowledgeFormSchema = z.object({
  note: z.string().trim().max(500, "Keep it under 500 characters."),
});

type AcknowledgeFormValues = z.infer<typeof acknowledgeFormSchema>;

interface AcknowledgeDialogProps {
  incident: Incident;
  onClose: () => void;
}

/**
 * Taking an incident on.
 *
 * The dialog spends a paragraph saying what acknowledging does not do,
 * because the obvious reading of the button is "make this go away". It does
 * not: the engine closes an incident when the readings recover (ADR-048), and
 * an operator who believes otherwise will acknowledge a live excursion and
 * walk away from a thawing freezer.
 */
export function AcknowledgeDialog({ incident, onClose }: AcknowledgeDialogProps) {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AcknowledgeFormValues>({
    resolver: zodResolver(acknowledgeFormSchema),
    defaultValues: { note: "" },
  });

  const mutation = useMutation({
    ...acknowledgeIncidentMutation(client),
    onSuccess: async () => {
      // The dashboard counts unresolved incidents, so its cards are stale too.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["incidents"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    await mutation.mutateAsync({
      id: incident.id,
      body: values.note ? { note: values.note } : {},
    });

    onClose();
  });

  return (
    <FormDialog
      open
      title={t("dialogAcknowledgeTitle")}
      description={`${INCIDENT_TYPE_LABELS[incident.type]} on ${incident.assetName ?? incident.devEui}, open since ${formatDateTime(incident.startedAt)}.`}
      submitLabel={t("acknowledge")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        <Alert tone="info" title={t("dialogAcknowledgeWhatTitle")} announce={false}>
          {t("dialogAcknowledgeWhatBody")}
        </Alert>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-md bg-surface-muted px-3 py-2.5 text-sm">
          <dt className="text-ink-muted">{tCommon("reading")}</dt>
          <dd className="text-right font-medium tabular-nums">
            {formatIncidentValue(incident.type, incident.triggerValue)}
          </dd>
          <dt className="text-ink-muted">{tCommon("threshold")}</dt>
          <dd className="text-right font-medium tabular-nums">
            {formatIncidentValue(incident.type, incident.thresholdValue)}
          </dd>
        </dl>

        <TextAreaField
          label={t("fieldNote")}
          hint={t("acknowledgeNoteHint")}
          {...(errors.note?.message === undefined ? {} : { error: errors.note.message })}
          {...register("note")}
        />
      </div>
    </FormDialog>
  );
}
